'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
  PathValue,
} from 'react-hook-form';

import { Checkbox, CheckboxProps } from '../checkbox';

export interface FormCheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<CheckboxProps, 'name' | 'defaultChecked' | 'checked' | 'onChange' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;
}

/**
 * Checkbox component integrated with React Hook Form
 * Handles boolean values automatically
 */
export function FormCheckbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, showError = true, ...props }: FormCheckboxProps<TFieldValues, TName>) {
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue: false as PathValue<TFieldValues, TName>,
  });

  // Handle change to ensure boolean value
  const handleChange = React.useCallback(
    (checked: boolean) => {
      onChange(checked);
    },
    [onChange]
  );

  return (
    <Checkbox
      {...props}
      ref={ref}
      checked={!!value}
      onChange={handleChange}
      error={showError ? error?.message : undefined}
      aria-invalid={!!error}
      aria-describedby={error ? `${name}-error` : undefined}
    />
  );
}

FormCheckbox.displayName = 'FormCheckbox';
